import { useState } from "react";
import axios from "axios";
import { Phone, Mail } from "lucide-react";
import { BRAND } from "../../data/content";
import { ChapterLabel, Container, CTAButton, FadeUp, MaskedLine } from "./ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-[2px] border border-line-light bg-surface px-4 py-3.5 font-sans text-sm font-light text-ivory placeholder:text-neutral-600 focus:border-champagne/60 focus:outline-none transition-colors duration-300";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-2 block font-display text-[10px] font-medium tracking-[0.18em] text-muted uppercase">
      {label}
    </span>
    {children}
  </label>
);

const RequestDesk = () => {
  const [form, setForm] = useState({
    message: "",
    name: "",
    contact: "",
    date: "",
    location: "",
    guests: "",
    budget: "",
    urgency: "standard",
  });
  const [state, setState] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await axios.post(`${API}/requests`, form);
      setState("success");
    } catch (err) {
      setState("error");
    }
  };

  return (
    <section id="demande" className="border-t border-line bg-ink py-28 md:py-40" data-testid="request-desk">
      <Container>
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          <div>
            <ChapterLabel num="06" label="The Request" />
            <h2 className="mt-10 font-serif font-medium leading-[0.95] text-ivory text-[clamp(44px,5.4vw,84px)]">
              <MaskedLine delay={0.1}>NE CHERCHEZ PAS.</MaskedLine>
              <MaskedLine delay={0.2} className="text-champagne">
                DEMANDEZ.
              </MaskedLine>
            </h2>
            <FadeUp delay={0.25} className="mt-8">
              <p className="max-w-[520px] font-sans text-[15px] font-light leading-[1.8] text-neutral-400 md:text-base">
                Décrivez-nous simplement ce dont vous avez besoin. Même si votre demande
                est complexe, urgente ou inhabituelle.
              </p>
              <p className="mt-4 max-w-[520px] font-sans text-[15px] font-light leading-[1.8] text-ivory md:text-base">
                Nous nous chargeons de trouver la solution.
              </p>
            </FadeUp>

            <FadeUp delay={0.35} className="mt-12">
              <div className="border border-line-light p-7 md:p-8" data-testid="private-line-block">
                <p className="font-display text-[10px] font-medium tracking-[0.22em] text-muted uppercase">
                  Ligne privée directe
                </p>
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  className="mt-3 block font-serif text-3xl font-medium text-ivory transition-colors duration-300 hover:text-champagne md:text-4xl"
                  data-testid="private-line-phone"
                >
                  {BRAND.phone}
                </a>
                <div className="mt-5 flex flex-wrap items-center gap-5">
                  <a
                    href={`tel:${BRAND.phoneTel}`}
                    className="inline-flex items-center gap-2 font-display text-[10px] font-semibold tracking-[0.16em] text-champagne uppercase hover:text-ivory transition-colors"
                    data-testid="private-line-call"
                  >
                    <Phone className="h-3.5 w-3.5" /> Appeler
                  </a>
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="inline-flex items-center gap-2 font-display text-[10px] font-semibold tracking-[0.16em] text-neutral-400 uppercase hover:text-ivory transition-colors"
                    data-testid="private-line-email"
                  >
                    <Mail className="h-3.5 w-3.5" /> {BRAND.email}
                  </a>
                </div>
              </div>
              <p className="mt-5 font-sans text-[12px] font-light leading-[1.7] text-neutral-500">
                Réponse sous 24h ouvrées. Confidentialité assurée.
              </p>
            </FadeUp>
          </div>

          <FadeUp delay={0.2}>
            {state === "success" ? (
              <div
                className="flex h-full min-h-[420px] flex-col items-start justify-center border border-champagne/30 bg-surface p-10"
                data-testid="request-success"
              >
                <p className="font-display text-[10px] font-medium tracking-[0.22em] text-champagne uppercase">
                  Demande reçue
                </p>
                <p className="mt-6 font-serif text-4xl font-medium leading-tight text-ivory md:text-5xl">
                  Nous nous en occupons.
                </p>
                <p className="mt-6 max-w-[380px] font-sans text-sm font-light leading-[1.8] text-neutral-400">
                  Votre demande a bien été transmise. Un conseiller vous répond sous 24h
                  ouvrées. Pour toute urgence, notre ligne privée reste disponible.
                </p>
                <CTAButton
                  variant="secondary"
                  className="mt-9"
                  testid="request-another-button"
                  onClick={() => {
                    setForm({ message: "", name: "", email: "", phone: "", date: "", location: "", guests: "", budget: "", urgency: "standard" });
                    setState("idle");
                  }}
                >
                  Nouvelle demande
                </CTAButton>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-line-light bg-surface p-7 md:p-10" data-testid="request-form">
                <Field label="Votre demande" testid="request-message-field">
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Décrivez simplement ce que vous recherchez…"
                    className={`${inputCls} resize-none`}
                    data-testid="request-message-input"
                  />
                </Field>

                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Date souhaitée" testid="request-date-field">
                    <input type="text" value={form.date} onChange={set("date")} placeholder="Optionnel" className={inputCls} data-testid="request-date-input" />
                  </Field>
                  <Field label="Lieu" testid="request-location-field">
                    <input type="text" value={form.location} onChange={set("location")} placeholder="Optionnel" className={inputCls} data-testid="request-location-input" />
                  </Field>
                  <Field label="Personnes" testid="request-guests-field">
                    <input type="text" value={form.guests} onChange={set("guests")} placeholder="Optionnel" className={inputCls} data-testid="request-guests-input" />
                  </Field>
                  <Field label="Budget indicatif" testid="request-budget-field">
                    <input type="text" value={form.budget} onChange={set("budget")} placeholder="Optionnel" className={inputCls} data-testid="request-budget-input" />
                  </Field>
                </div>

                <div className="mt-6">
                  <Field label="Niveau d'urgence" testid="request-urgency-field">
                    <div className="flex gap-2" data-testid="request-urgency-group">
                      {[
                        ["standard", "Standard"],
                        ["prioritaire", "Prioritaire"],
                        ["urgent", "Urgent"],
                      ].map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, urgency: val }))}
                          className={`flex-1 rounded-[2px] border px-3 py-3 font-display text-[10px] font-semibold tracking-[0.14em] uppercase transition-colors duration-300 ${
                            form.urgency === val
                              ? "border-champagne/70 bg-champagne/10 text-champagne"
                              : "border-line-light text-neutral-500 hover:border-neutral-600 hover:text-neutral-300"
                          }`}
                          data-testid={`request-urgency-${val}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <Field label="Votre nom" testid="request-name-field">
                    <input required type="text" value={form.name} onChange={set("name")} placeholder="Nom" className={inputCls} data-testid="request-name-input" />
                  </Field>
                  <Field label="Votre email *" testid="request-email-field">
                    <input required type="email" value={form.email} onChange={set("email")} placeholder="email@exemple.com" className={inputCls} data-testid="request-email-input" />
                  </Field>
                  <Field label="Votre téléphone *" testid="request-phone-field">
                    <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+33 …" className={inputCls} data-testid="request-phone-input" />
                  </Field>
                </div>

                {state === "error" && (
                  <p className="mt-5 font-sans text-[13px] text-red-400/90" data-testid="request-error">
                    Une erreur est survenue. Merci de réessayer ou d'utiliser notre ligne directe.
                  </p>
                )}

                <CTAButton type="submit" disabled={state === "sending"} className="mt-8 w-full" testid="request-submit-button">
                  {state === "sending" ? "Transmission…" : "Confier ma demande"}
                </CTAButton>
                <p className="mt-4 text-center font-sans text-[11px] font-light text-neutral-600">
                  Réponse sous 24h ouvrées · Confidentialité assurée
                </p>
              </form>
            )}
          </FadeUp>
        </div>
      </Container>
    </section>
  );
};

export default RequestDesk;
